/**
 * ArchLang v0.10 — matrix (DSM) representation data model (S13b, engine layer).
 *
 * `computeMatrixData(view, plan, ctx)` turns a `view` carrying a `matrix` clause
 * into a **`MatrixData`**: a Design Structure Matrix over the shown modules. Each
 * cell (row → col) records whether a call edge couples the row to the column, and
 * carries the deriving process + edge ids so the board (`ViewMatrix`, S15) can
 * scaffold `row > col` from an empty-cell click (S13a `scaffoldEdgeEdit`). Pure
 * over the derived graph — no scene types, no rendering.
 *
 * ## Axes
 * A canonical DSM is SQUARE — the same module set on both axes, so the diagonal
 * is module-vs-itself. Omitting `axis`/`rows`/`cols` gives the default square
 * axis over the shown modules (ordered lexicographically by key); `axis <getter>`
 * groups that shared axis by the getter's distinct values. `rows`/`cols` diverge
 * only for a RECTANGULAR coupling matrix. A getter axis is a `grouped` axis; a
 * key axis is scaffold-able (its labels are module keys `scaffoldEdgeEdit` names).
 *
 * ## Ordering & clustering (square only)
 * `order by cluster`/`layer` and `cluster` run graph analysis over the coupling
 * cells (`dsm.ts`): a sink-first permutation drops forward coupling into the lower
 * triangle and leaves feedback (cycle back-edges) above the diagonal, and
 * `cluster` boxes strongly-connected groups (cycles) on the diagonal. `order by
 * <getter>` sorts deterministically with no graph analysis.
 *
 * ## Determinism
 * Axes are sorted; per-cell `edgeIds` are sorted; `deriving` is the process of the
 * lexicographically smallest coupling edge; every ordering tie-breaks by label. A
 * pure function of `(view, plan, model, graph)`.
 */

import type { MatrixClause, ViewDeclV2 } from "./v010-ast.js";
import type { CallEdge } from "./graph.js";
import { isCallEdge } from "./graph.js";
import type { EvalContext, ElementNode } from "./selector-eval.js";
import { buildAxis, axisValuesOf, type Axis } from "./view-axis.js";
import { partitionDsm, type DsmEdge } from "./dsm.js";
import { edgeIdOf, type ViewProjectionPlan } from "./view-projection.js";

// =============================================================================
// Shape
// =============================================================================

/** One DSM cell — the coupling (row → col) + its provenance. */
export interface MatrixCell {
  /** True IFF at least one call edge couples the row axis entry to the column. */
  readonly present: boolean;
  /** Canonical ids ({@link edgeIdOf}) of every coupling call edge — sorted. */
  readonly edgeIds: readonly string[];
  /** The deriving process (of the smallest coupling edge) — an edit anchor hint. */
  readonly deriving?: string;
  /** True when this present coupling is a back-edge (above the diagonal) — a cycle. */
  readonly feedback?: boolean;
}

/** A strongly-connected diagonal block (a cycle box) over the ordered axis. */
export interface MatrixBlock {
  /** Ordered-axis positions in this block — a contiguous run. */
  readonly members: readonly number[];
  /** True when the block is a real cycle (≥2 members, or a self-loop). */
  readonly isCycle: boolean;
  /** Topological layer — 0 = sink, rising toward sources. */
  readonly layer: number;
}

/** The full DSM projection — square-ish axes + a `cells[row][col]` grid. */
export interface MatrixData {
  /** Row axis entries (module keys, or grouped getter values) — ordered. */
  readonly axisRows: readonly string[];
  /** Column axis entries — ordered. */
  readonly axisCols: readonly string[];
  /** `cells[rowIndex][colIndex]` — aligned to {@link axisRows} / {@link axisCols}. */
  readonly cells: readonly (readonly MatrixCell[])[];
  /** True when the ROW axis is a grouped getter axis (labels ≠ scaffold-able keys). */
  readonly rowsGrouped: boolean;
  /** True when the COLUMN axis is a grouped getter axis. */
  readonly colsGrouped: boolean;
  /** True when the DSM is square (shared axis, meaningful diagonal). */
  readonly square: boolean;
  /** Cycle blocks to box on the diagonal — present only when `cluster` is requested. */
  readonly blocks?: readonly MatrixBlock[];
}

// =============================================================================
// computeMatrixData
// =============================================================================

/**
 * Project a `view` with a `matrix` clause into a {@link MatrixData}. Returns
 * `null` for a view that carries no `MatrixClause`.
 */
export function computeMatrixData(
  view: ViewDeclV2,
  plan: ViewProjectionPlan | null,
  ctx: EvalContext,
): MatrixData | null {
  const matrix = view.clauses.find((c): c is MatrixClause => c.kind === "matrix");
  if (!matrix) return null;

  // Shown modules — the DSM universe (call edges couple modules).
  const modules = ctx.universe.nodes.filter(
    (n) => n.structuralKind === "module" && (plan ? plan.shown.has(n.key) : true),
  );
  const shownKeys = new Set(modules.map((m) => m.key));

  // Square unless divergent rows/cols. The square axis groups by `axis` (or the
  // raw module keys); the rectangular axes come from `rows`/`cols`.
  const square = !matrix.rows && !matrix.cols;
  const rowAxis = square ? buildAxis(matrix.axis, modules, ctx) : buildAxis(matrix.rows, modules, ctx);
  const colAxis = square ? rowAxis : buildAxis(matrix.cols, modules, ctx);

  // Bucket every coupling call edge into its (rowLabel, colLabel) cells. An edge
  // whose endpoint maps to several labels (list-valued aspect axis) fans to each.
  const buckets = new Map<string, { edgeIds: string[]; procs: string[] }>();
  const callEdges: CallEdge[] = (ctx.graph?.edges ?? []).filter(isCallEdge);
  for (const e of callEdges) {
    const from = e.fromModulePath.join(".");
    const to = e.toModulePath.join(".");
    if (!shownKeys.has(from) || !shownKeys.has(to)) continue;
    const rowLabels = rowAxis.labelsOf.get(from);
    const colLabels = colAxis.labelsOf.get(to);
    if (!rowLabels || !colLabels) continue;
    for (const rl of rowLabels) {
      for (const cl of colLabels) {
        const bkey = rl + "\0" + cl; // NUL-keyed like view-grid — space-containing group labels must not collide (r42)
        const b = buckets.get(bkey) ?? { edgeIds: [], procs: [] };
        b.edgeIds.push(edgeIdOf(e));
        if (e.processName) b.procs.push(e.processName);
        buckets.set(bkey, b);
      }
    }
  }

  const cellOf = (rl: string, cl: string): MatrixCell => {
    const b = buckets.get(rl + "\0" + cl);
    if (!b || b.edgeIds.length === 0) return { present: false, edgeIds: [] };
    const edgeIds = [...b.edgeIds].sort();
    const deriving = b.procs.length > 0 ? [...b.procs].sort()[0] : undefined;
    return { present: true, edgeIds, deriving };
  };

  // Base grid in the axes' natural (sorted) order.
  const baseCells: MatrixCell[][] = rowAxis.labels.map((rl) =>
    colAxis.labels.map((cl) => cellOf(rl, cl)),
  );

  // Square DSM with an `order`/`cluster` clause: permute the shared axis.
  if (square && (matrix.order || matrix.cluster)) {
    return orderedSquare(matrix, rowAxis, baseCells, modules, ctx);
  }

  return {
    axisRows: rowAxis.labels,
    axisCols: colAxis.labels,
    cells: baseCells,
    rowsGrouped: rowAxis.grouped,
    colsGrouped: colAxis.grouped,
    square,
  };
}

/**
 * Apply a square DSM's `order`/`cluster` clause: resolve a permutation of the
 * shared axis, then reorder labels + cells and mark feedback / cycle blocks.
 */
function orderedSquare(
  matrix: MatrixClause,
  axis: Axis,
  baseCells: readonly (readonly MatrixCell[])[],
  modules: readonly ElementNode[],
  ctx: EvalContext,
): MatrixData {
  const labels = axis.labels;
  const n = labels.length;

  const wantCluster = matrix.cluster === true || matrix.order?.strategy.kind === "cluster";
  const wantLayer = matrix.order?.strategy.kind === "layer";
  const getter = matrix.order?.strategy.kind === "getter" ? matrix.order.strategy.getter : undefined;

  let order: number[];
  let blocks: MatrixBlock[] | undefined;
  let feedback: ReadonlySet<string> = new Set();

  if (wantCluster || wantLayer) {
    const edges: DsmEdge[] = [];
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) if (baseCells[r]![c]!.present) edges.push([r, c]);
    }
    const part = partitionDsm(n, edges);
    order = [...part.order];
    feedback = part.feedback;
    if (wantCluster) {
      // `cluster` + `order by <getter>`: the getter sorts WITHIN each block.
      if (getter) order = reorderWithinBlocks(part.blocks, labels, getter, modules, ctx);
      const pos = positionsOf(order);
      blocks = part.blocks.map((b) => ({
        members: [...b.members].map((oi) => pos[oi]!).sort((x, y) => x - y),
        isCycle: b.isCycle,
        layer: b.layer,
      }));
    }
  } else if (getter) {
    order = sortByGetter(labels, getter, modules, ctx);
  } else {
    order = labels.map((_, i) => i);
  }

  const axisLabels = order.map((oi) => labels[oi]!);
  const cells: MatrixCell[][] = order.map((or) =>
    order.map((oc) => {
      const cell = baseCells[or]![oc]!;
      return cell.present && feedback.has(or + "->" + oc) ? { ...cell, feedback: true } : cell;
    }),
  );

  return {
    axisRows: axisLabels,
    axisCols: axisLabels,
    cells,
    rowsGrouped: axis.grouped,
    colsGrouped: axis.grouped,
    square: true,
    ...(blocks ? { blocks } : {}),
  };
}

/** Ordered position of each original index. */
function positionsOf(order: readonly number[]): number[] {
  const pos: number[] = [];
  order.forEach((orig, p) => (pos[orig] = p));
  return pos;
}

/** The getter sort key for an axis label — the module's getter value, or the label. */
function getterKeyOf(
  label: string,
  getter: Parameters<typeof axisValuesOf>[0],
  modByKey: ReadonlyMap<string, ElementNode>,
  ctx: EvalContext,
): string {
  const m = modByKey.get(label);
  if (!m) return label; // grouped label — no backing module; sort by label text.
  const vals = axisValuesOf(getter, m, ctx);
  return vals.length > 0 ? vals[0]! : "￿"; // missing value → sorts last.
}

/** Order the whole axis by a getter, tie-breaking on the label. */
function sortByGetter(
  labels: readonly string[],
  getter: Parameters<typeof axisValuesOf>[0],
  modules: readonly ElementNode[],
  ctx: EvalContext,
): number[] {
  const modByKey = new Map(modules.map((m) => [m.key, m]));
  return labels
    .map((_, i) => i)
    .sort((a, b) => {
      const ka = getterKeyOf(labels[a]!, getter, modByKey, ctx);
      const kb = getterKeyOf(labels[b]!, getter, modByKey, ctx);
      if (ka !== kb) return ka < kb ? -1 : 1;
      return labels[a]! < labels[b]! ? -1 : labels[a]! > labels[b]! ? 1 : 0;
    });
}

/** Keep cluster blocks contiguous, but order members within each block by a getter. */
function reorderWithinBlocks(
  blocks: readonly { readonly members: readonly number[] }[],
  labels: readonly string[],
  getter: Parameters<typeof axisValuesOf>[0],
  modules: readonly ElementNode[],
  ctx: EvalContext,
): number[] {
  const modByKey = new Map(modules.map((m) => [m.key, m]));
  const out: number[] = [];
  for (const b of blocks) {
    const sorted = [...b.members].sort((a, z) => {
      const ka = getterKeyOf(labels[a]!, getter, modByKey, ctx);
      const kz = getterKeyOf(labels[z]!, getter, modByKey, ctx);
      if (ka !== kz) return ka < kz ? -1 : 1;
      return labels[a]! < labels[z]! ? -1 : labels[a]! > labels[z]! ? 1 : 0;
    });
    out.push(...sorted);
  }
  return out;
}
